import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminAuthService from '../services/adminAuthService';
import {
  listSupportTickets,
  getTicketDetail,
  sendAdminMessage,
  updateTicketStatus,
  claimTicket,
  assignTicket,
  listAgents,
  getUserInfo,
} from '../services/adminMessagesService';
import './MessagesPage.css';

// ── Constants ─────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '',            label: 'All Status' },
  { value: 'open',        label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved',    label: 'Resolved' },
  { value: 'closed',      label: 'Closed' },
];

const CATEGORY_OPTIONS = [
  { value: '',           label: 'All Categories' },
  { value: 'order',      label: 'Order Issue' },
  { value: 'product',    label: 'Product Problem' },
  { value: 'payment',    label: 'Payment' },
  { value: 'delivery',   label: 'Delivery' },
  { value: 'payout',     label: 'Payout' },
  { value: 'listing',    label: 'Listing' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'account',    label: 'Account' },
  { value: 'general',    label: 'General Help' },
];

const PRIORITY_OPTIONS = [
  { value: '',       label: 'All Priority' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high',   label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low',    label: 'Low' },
];

const ASSIGN_OPTIONS = [
  { value: '',           label: 'All Assignments' },
  { value: 'unassigned', label: 'Unassigned' },
];

// ── Status colours ────────────────────────────────────────────

const statusMeta = {
  open:        { label: 'Open',        color: '#f59e0b', bg: '#fffbeb' },
  in_progress: { label: 'In Progress', color: '#3b82f6', bg: '#eff6ff' },
  resolved:    { label: 'Resolved',    color: '#039855', bg: '#ecfdf5' },
  closed:      { label: 'Closed',      color: '#94a3b8', bg: '#f8fafc' },
  cancelled:   { label: 'Cancelled',   color: '#ef4444', bg: '#fef2f2' },
};

const priorityMeta = {
  urgent: { label: 'Urgent', color: '#ef4444' },
  high:   { label: 'High',   color: '#f59e0b' },
  normal: { label: 'Normal', color: '#3b82f6' },
  low:    { label: 'Low',    color: '#94a3b8' },
};

// ── Helpers ───────────────────────────────────────────────────

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000)   return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const fmtMsgTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const userName = (u) => {
  if (!u) return 'Unknown';
  const full = `${u.fname || ''} ${u.lname || ''}`.trim();
  return full || u.name || u.email || 'Unknown';
};

const catLabel = (v) => CATEGORY_OPTIONS.find((o) => o.value === v)?.label || v || '';

// ── Main page ─────────────────────────────────────────────────

const MessagesPage = () => {
  const [tickets, setTickets]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [filterStatus, setStatus]     = useState('');
  const [filterCat, setCat]           = useState('');
  const [filterPri, setPri]           = useState('');
  const [filterAssign, setAssign]     = useState('');
  const [search, setSearch]           = useState('');
  const [selected, setSelected]       = useState(null);
  const [agents, setAgents]           = useState([]);
  const [currentUserId, setCurrentUid]= useState(null);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, uid] = await Promise.all([
        listSupportTickets({
          status:     filterStatus || undefined,
          category:   filterCat    || undefined,
          priority:   filterPri    || undefined,
          assignedTo: filterAssign || undefined,
        }),
        AdminAuthService.getCurrentUserId(),
      ]);
      setTickets(list);
      setCurrentUid(uid);
    } catch (e) {
      setError(e.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCat, filterPri, filterAssign]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  useEffect(() => {
    listAgents().then(setAgents).catch(() => {});
  }, []);

  const filtered = tickets.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (t.subject || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="mp-root">
      {/* ── Left panel ─────────────────────────────────────── */}
      <div className="mp-left">
        <div className="mp-left-header">
          <div className="mp-title-row">
            <h2 className="mp-title">Support Inbox</h2>
            <span className="mp-count">{filtered.length}</span>
          </div>

          {/* Filters */}
          <input
            className="mp-search"
            placeholder="Search tickets…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="mp-filters">
            <select value={filterStatus} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={filterCat} onChange={(e) => setCat(e.target.value)}>
              {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={filterPri} onChange={(e) => setPri(e.target.value)}>
              {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={filterAssign} onChange={(e) => setAssign(e.target.value)}>
              {ASSIGN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Ticket list */}
        <div className="mp-ticket-list">
          {loading && <div className="mp-loading"><div className="mp-spinner" />Loading tickets…</div>}
          {error   && <div className="mp-error">{error}<button onClick={loadTickets}>Retry</button></div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="mp-empty">No tickets found</div>
          )}
          {filtered.map((t) => (
            <TicketRow
              key={t.$id || t.conversationId}
              ticket={t}
              active={selected?.id === (t.$id || t.conversationId)}
              onClick={() => setSelected({ id: t.$id || t.conversationId, data: t })}
            />
          ))}
        </div>
      </div>

      {/* ── Right panel ────────────────────────────────────── */}
      <div className="mp-right">
        {selected ? (
          <TicketDetail
            key={selected.id}
            conversationId={selected.id}
            ticket={selected.data}
            currentUserId={currentUserId}
            agents={agents}
            onRefresh={() => { loadTickets(); }}
            onStatusChange={(newStatus) => {
              setSelected((s) => s ? { ...s, data: { ...s.data, status: newStatus } } : s);
              setTickets((prev) =>
                prev.map((t) =>
                  (t.$id || t.conversationId) === selected.id
                    ? { ...t, status: newStatus }
                    : t
                )
              );
            }}
          />
        ) : (
          <div className="mp-no-select">
            <div className="mp-no-select-icon">💬</div>
            <p>Select a ticket to view the conversation</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Ticket row ────────────────────────────────────────────────

const TicketRow = ({ ticket, active, onClick }) => {
  const s = statusMeta[ticket.status] || statusMeta.open;
  const p = priorityMeta[ticket.priority];
  return (
    <div className={`mp-ticket-row${active ? ' mp-ticket-row--active' : ''}`} onClick={onClick}>
      <div className="mp-ticket-row-top">
        <span className="mp-ticket-subject">{ticket.subject || 'No subject'}</span>
        <span className="mp-ticket-time">{fmtTime(ticket.lastMessageAt || ticket.createdAt)}</span>
      </div>
      <div className="mp-ticket-row-bottom">
        <span className="mp-status-chip" style={{ color: s.color, background: s.bg }}>
          {s.label}
        </span>
        {ticket.category && (
          <span className="mp-cat-chip">{catLabel(ticket.category)}</span>
        )}
        {p && (
          <span className="mp-pri-chip" style={{ color: p.color }}>
            {p.label}
          </span>
        )}
        {!ticket.assignedTo && (
          <span className="mp-unassigned-chip">Unassigned</span>
        )}
      </div>
      {ticket.lastMessageText && (
        <div className="mp-ticket-preview">{ticket.lastMessageText}</div>
      )}
    </div>
  );
};

// ── Ticket detail + thread ────────────────────────────────────

const TicketDetail = ({
  conversationId, ticket, currentUserId, agents, onRefresh, onStatusChange,
}) => {
  const [messages, setMessages]     = useState([]);
  const [participants, setParticipants] = useState([]);
  const [userMap, setUserMap]       = useState({});
  const [msgLoading, setMsgLoading] = useState(true);
  const [msgError, setMsgError]     = useState(null);
  const [replyText, setReplyText]   = useState('');
  const [sending, setSending]       = useState(false);
  const [actionLoading, setAction]  = useState(false);
  const [assignTarget, setAssignTarget] = useState('');
  const bottomRef = useRef(null);

  const loadThread = useCallback(async () => {
    try {
      setMsgLoading(true);
      setMsgError(null);
      const { messages: msgs, participants: parts } = await getTicketDetail(conversationId);
      setMessages(msgs || []);
      setParticipants(parts || []);

      // Fetch user info for all senders + participants
      const ids = [...new Set([
        ...(msgs || []).map((m) => m.senderId),
        ...(parts || []).map((p) => p.participantId),
      ].filter(Boolean))];
      const entries = await Promise.all(ids.map(async (id) => [id, await getUserInfo(id)]));
      setUserMap(Object.fromEntries(entries));
    } catch (e) {
      setMsgError(e.message || 'Failed to load thread');
    } finally {
      setMsgLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { loadThread(); }, [loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = replyText.trim();
    if (!text) return;
    setSending(true);
    try {
      const msg = await sendAdminMessage(conversationId, text);
      setReplyText('');
      setMessages((prev) => [...prev, msg]);
    } catch (e) {
      alert(`Send failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleClaim = async () => {
    setAction(true);
    try {
      await claimTicket(conversationId);
      onStatusChange('in_progress');
      onRefresh();
    } catch (e) {
      alert(`Claim failed: ${e.message}`);
    } finally {
      setAction(false);
    }
  };

  const handleStatus = async (newStatus) => {
    setAction(true);
    try {
      await updateTicketStatus(conversationId, newStatus);
      onStatusChange(newStatus);
      setMessages((prev) => [...prev, {
        id: Date.now().toString(),
        senderId: currentUserId,
        body: `Ticket status changed to: ${newStatus.replace('_', ' ')}`,
        messageType: 'system',
        createdAt: new Date().toISOString(),
      }]);
    } catch (e) {
      alert(`Update failed: ${e.message}`);
    } finally {
      setAction(false);
    }
  };

  const handleAssign = async () => {
    if (!assignTarget) return;
    setAction(true);
    try {
      await assignTicket(conversationId, assignTarget);
      onRefresh();
      setAssignTarget('');
    } catch (e) {
      alert(`Assign failed: ${e.message}`);
    } finally {
      setAction(false);
    }
  };

  const s = statusMeta[ticket.status] || statusMeta.open;
  const assignedUser = ticket.assignedTo ? userMap[ticket.assignedTo] : null;

  return (
    <div className="mp-detail">
      {/* ── Detail header ─────────────────────────────────── */}
      <div className="mp-detail-header">
        <div className="mp-detail-header-top">
          <h3 className="mp-detail-subject">{ticket.subject || 'No subject'}</h3>
          <span className="mp-status-chip mp-status-chip--lg" style={{ color: s.color, background: s.bg }}>
            {s.label}
          </span>
        </div>
        <div className="mp-detail-meta">
          {ticket.category && <span className="mp-cat-chip">{catLabel(ticket.category)}</span>}
          {ticket.priority && (
            <span className="mp-pri-chip" style={{ color: priorityMeta[ticket.priority]?.color }}>
              {priorityMeta[ticket.priority]?.label || ticket.priority}
            </span>
          )}
          <span className="mp-detail-meta-text">
            Opened {fmtTime(ticket.createdAt)}
          </span>
          {assignedUser && (
            <span className="mp-detail-meta-text">
              Assigned to: <strong>{userName(assignedUser)}</strong>
            </span>
          )}
          {!ticket.assignedTo && (
            <span className="mp-unassigned-chip">Unassigned</span>
          )}
        </div>
      </div>

      {/* ── Action bar ────────────────────────────────────── */}
      <div className="mp-action-bar">
        {/* Claim */}
        {ticket.status === 'open' && (
          <button
            className="mp-btn mp-btn--primary"
            onClick={handleClaim}
            disabled={actionLoading}
          >
            {actionLoading ? '…' : 'Claim Ticket'}
          </button>
        )}

        {/* Status actions */}
        {ticket.status === 'in_progress' && (
          <>
            <button
              className="mp-btn mp-btn--success"
              onClick={() => handleStatus('resolved')}
              disabled={actionLoading}
            >
              Mark Resolved
            </button>
            <button
              className="mp-btn mp-btn--ghost"
              onClick={() => handleStatus('closed')}
              disabled={actionLoading}
            >
              Close
            </button>
          </>
        )}
        {ticket.status === 'resolved' && (
          <>
            <button
              className="mp-btn mp-btn--ghost"
              onClick={() => handleStatus('in_progress')}
              disabled={actionLoading}
            >
              Reopen
            </button>
            <button
              className="mp-btn mp-btn--ghost"
              onClick={() => handleStatus('closed')}
              disabled={actionLoading}
            >
              Close
            </button>
          </>
        )}
        {ticket.status === 'closed' && (
          <button
            className="mp-btn mp-btn--ghost"
            onClick={() => handleStatus('open')}
            disabled={actionLoading}
          >
            Reopen
          </button>
        )}

        {/* Assign */}
        {agents.length > 0 && (
          <div className="mp-assign-row">
            <select
              value={assignTarget}
              onChange={(e) => setAssignTarget(e.target.value)}
              className="mp-assign-select"
            >
              <option value="">Assign to agent…</option>
              {agents.map((a) => (
                <option key={a.$id || a.userId} value={a.$id || a.userId}>
                  {userName(a)}
                </option>
              ))}
            </select>
            <button
              className="mp-btn mp-btn--ghost"
              onClick={handleAssign}
              disabled={!assignTarget || actionLoading}
            >
              Assign
            </button>
          </div>
        )}
      </div>

      {/* ── Message thread ────────────────────────────────── */}
      <div className="mp-thread">
        {msgLoading && <div className="mp-loading"><div className="mp-spinner" />Loading…</div>}
        {msgError   && <div className="mp-error">{msgError}<button onClick={loadThread}>Retry</button></div>}
        {!msgLoading && messages.map((msg) => {
          const isSystem = msg.messageType === 'system';
          const isMe = msg.senderId === currentUserId;
          const sender = userMap[msg.senderId];

          if (isSystem) {
            return (
              <div key={msg.id || msg.messageId || msg.$id} className="mp-msg-system">
                {msg.body}
              </div>
            );
          }

          return (
            <div
              key={msg.id || msg.messageId || msg.$id}
              className={`mp-msg${isMe ? ' mp-msg--me' : ''}`}
            >
              {!isMe && (
                <div className="mp-msg-avatar">
                  {(userName(sender)[0] || '?').toUpperCase()}
                </div>
              )}
              <div className="mp-msg-body-wrap">
                {!isMe && (
                  <span className="mp-msg-sender">{userName(sender)}</span>
                )}
                <div className="mp-msg-bubble">
                  {msg.body}
                </div>
                <span className="mp-msg-time">{fmtMsgTime(msg.createdAt)}</span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* ── Reply box ─────────────────────────────────────── */}
      {ticket.status !== 'closed' && ticket.status !== 'cancelled' && (
        <div className="mp-reply">
          <textarea
            className="mp-reply-input"
            placeholder="Reply to this ticket…"
            rows={3}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend();
            }}
          />
          <button
            className="mp-btn mp-btn--primary"
            onClick={handleSend}
            disabled={sending || !replyText.trim()}
          >
            {sending ? 'Sending…' : 'Send Reply'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
